import { AdminCertification, AdminCertificationStatus, AdminModule } from 'types/admin/modules';
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export type CertificationsData = Pick<AdminModule, 'certifications'>;
type State = CertificationsData & { initializing: boolean; loading: boolean };

type Actions = {
  setInitialCertifications: (certifications: AdminCertification[]) => void;
  addCertification: () => void;
  deleteCertification: (id: string) => void;
  updateCertificationTitle: (id: string, title: string) => void;
  updateCertificationStatus: (id: string, status: AdminCertificationStatus) => void;
  updateCertification: (certification: AdminCertification & { newId: string }) => void;
  reset: () => void;
  setLoadingInitialData: (initializing: boolean) => void;
  setLoading: (loading: boolean) => void;
};

export const useCertificationsStore = create<State & Actions>((set, get, store) => ({
  certifications: [],
  initializing: false,
  loading: false,
  setInitialCertifications: (certifications: AdminCertification[]) => set((state: State) => ({ certifications })),
  addCertification: () => set((state: State) => ({ certifications: [...state.certifications, emptyCertification()] })),
  deleteCertification: (id: string) =>
    set((state: State) => ({ certifications: state.certifications.filter((certification) => certification.id !== id) })),
  updateCertificationTitle: (id: string, title: string) =>
    set((state: State) => ({
      certifications: state.certifications.map((certification) => (certification.id !== id ? certification : { ...certification, title })),
    })),
  updateCertificationStatus: (id: string, status: AdminCertificationStatus) =>
    set((state: State) => ({
      certifications: state.certifications.map((certification) => (certification.id !== id ? certification : { ...certification, status })),
    })),
  updateCertification: (certification: AdminCertification & { newId: string }) =>
    set((state: State) => ({
      certifications: state.certifications.map((c) =>
        c.id !== certification.id
          ? c
          : {
              id: certification.newId,
              title: certification.title,
              shortDescription: certification.shortDescription,
              fullDescription: certification.fullDescription,
              status: certification.status,
              enterpoint: certification.enterpoint,
              prerequisites: certification.prerequisites,
              numberOfQuestions: certification.numberOfQuestions,
            }
      ),
    })),
  reset: () => set(store.getInitialState()),
  setLoadingInitialData: (initializing: boolean) => set(() => ({ initializing })),
  setLoading: (loading: boolean) => set(() => ({ loading })),
}));

const emptyCertification: () => AdminCertification = () => ({
  id: uuidv4(),
  title: 'New certification',
  shortDescription: '',
  fullDescription: '',
  enterpoint: '',
  status: 'Hidden',
  prerequisites: [],
  numberOfQuestions: 0,
});
